import {
  createSlice,
  createListenerMiddleware,
  PayloadAction,
  createAsyncThunk,
  createSelector,
} from "@reduxjs/toolkit";
import { ChargeableItem } from "../pages/usage-ng/utils";
import { fetchOrgInvites, OrgInvite } from "./userDataSlice";
import { automatedRequest, homeLink, linksOf, request } from "../services/cpln";
import { RootState } from ".";
import { Org } from "../schema/types/org/entity";
import { getFirebaseApp } from "./firebase";
import { getDownloadURL, getMetadata, getStorage, listAll, ListResult, ref } from "firebase/storage";
import { Kind } from "../schema/types/base";

export interface CustomLogo {
  slug: string;
  url: string;
}

export interface SliceDomain {
  name: string;
  status: {
    endpoints: {
      url: string;
      workloadLink: string;
    }[];
  };
}

export interface RestState {
  // terraform
  terraformSupportedKinds: Kind[];

  // k8s crd
  k8sCrdSupportedKinds: Kind[];

  // task
  taskRequestCounter: number;
  newInviteCounter: number;
  intervalMs: number;

  // domain
  domainItems: SliceDomain[];
  isLoadingDomains: boolean;
  domainRequestCounter: number;

  // custom logo
  lightSlug: string;
  darkSlug: string;
  customLogos: CustomLogo[];

  // billing alert
  amount: number;
  enabled: boolean;

  // billing
  ChargeableItems: ChargeableItem[];
  ReferralId: string;
}

const initialState: RestState = {
  terraformSupportedKinds: [],
  k8sCrdSupportedKinds: [],
  taskRequestCounter: 0,
  newInviteCounter: 0,
  intervalMs: 45 * 1000,
  domainItems: [],
  isLoadingDomains: false,
  domainRequestCounter: 0,
  lightSlug: "",
  darkSlug: "",
  customLogos: [],
  amount: 0,
  enabled: false,
  ChargeableItems: [],
  ReferralId: "",
};

export const restSlice = createSlice({
  name: "rest",
  initialState,
  reducers: {
    setTerraformSupportedKinds(state, action: PayloadAction<Kind[]>) {
      state.terraformSupportedKinds = action.payload;
    },
    setK8sCrdSupportedKinds(state, action: PayloadAction<Kind[]>) {
      state.k8sCrdSupportedKinds = action.payload;
    },
    incrementNewInviteCounter(state) {
      state.newInviteCounter += 1;
    },
    incrementTaskRequestCounter(state) {
      state.taskRequestCounter += 1;
    },
    setLightLogoSlug(state, action: PayloadAction<string>) {
      state.lightSlug = action.payload;
    },
    setDarkLogoSlug(state, action: PayloadAction<string>) {
      state.darkSlug = action.payload;
    },
    setReferralId(state, action: PayloadAction<string>) {
      state.ReferralId = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      // Handle fetch account summaries states
      .addCase(fetchDomainItems.pending, (state) => {
        state.isLoadingDomains = true;
      })
      .addCase(fetchDomainItems.fulfilled, (state, action) => {
        state.domainItems = action.payload;
        state.isLoadingDomains = false;
      })
      .addCase(fetchDomainItems.rejected, (state) => {
        state.domainItems = [];
        state.isLoadingDomains = false;
      })
      // Handle fetch account summaries states
      .addCase(fetchCustomLogo.pending, (state) => {
        state.customLogos = [];
        state.isLoadingDomains = true;
      })
      .addCase(fetchCustomLogo.fulfilled, (state, action) => {
        state.customLogos = action.payload;
        state.isLoadingDomains = false;
      })
      .addCase(fetchCustomLogo.rejected, (state) => {
        state.customLogos = [];
        state.isLoadingDomains = false;
      })
      // Handle fetch account summaries states
      .addCase(fetchChargeableItems.pending, (state) => {
        state.ChargeableItems = [];
      })
      .addCase(fetchChargeableItems.fulfilled, (state, action) => {
        state.ChargeableItems = action.payload;
      })
      .addCase(fetchChargeableItems.rejected, (state) => {
        state.ChargeableItems = [];
      });
  },
});

export const {
  setTerraformSupportedKinds,
  setK8sCrdSupportedKinds,
  incrementNewInviteCounter,
  incrementTaskRequestCounter,
  setLightLogoSlug,
  setDarkLogoSlug,
} = restSlice.actions;

export const fetchTasks = createAsyncThunk("rest/fetchTasks", async (_, { getState, dispatch }) => {
  // TODO test what happens when fails
  // TODO remove?
  // cplnLog(`Checking tasks | ${new Date().toISOString()} | Request Counter: ${this.requestCounter}`);
  let nextLink: string | undefined = "/task/-forme";
  const invites: OrgInvite[] = [];
  while (nextLink) {
    // TODO why do we need automated request?
    const { data } = await automatedRequest({ url: nextLink });
    for (let item of data.items) {
      invites.push(item as any);
    }
    nextLink = linksOf(data).next;
  }
  const newInviteIds = invites.map((i) => i.id);

  const state = getState() as RootState;
  const oldInviteIds = state.userData.orgInvites.map((i) => i.id);
  let foundNewInvite = false;
  for (let newId of newInviteIds) {
    if (!oldInviteIds.includes(newId)) {
      foundNewInvite = true;
    }
  }

  if (foundNewInvite) {
    dispatch(incrementNewInviteCounter());
    dispatch(fetchOrgInvites());
  }
  dispatch(incrementTaskRequestCounter());
});

export const fetchDomainItems = createAsyncThunk("rest/fetchDomainItems", async (_, { getState, dispatch }) => {
  let nextLink: string | undefined = homeLink("domain");
  let items: SliceDomain[] = [];
  while (nextLink) {
    const { data } = await automatedRequest({ url: nextLink });
    for (let item of data.items) {
      items.push({
        ...item,
        status: { ...item.status, endpoints: item.status.endpoints || [] },
      });
    }
    nextLink = linksOf(data).next;
  }
  return items;
});

const LOGO_CACHE_PREFIX = "custom-logo-cache-";
const LOGO_CACHE_MAX_AGE_MS = 24 * 60 * 60 * 1000; // 24 hours

function getCachedLogoUrl(slug: string): string | null {
  try {
    const raw = localStorage.getItem(`${LOGO_CACHE_PREFIX}${slug}`);
    if (!raw) return null;
    const entry = JSON.parse(raw) as { url: string; timestamp: number };
    if (Date.now() - entry.timestamp > LOGO_CACHE_MAX_AGE_MS) {
      localStorage.removeItem(`${LOGO_CACHE_PREFIX}${slug}`);
      return null;
    }
    return entry.url;
  } catch {
    return null;
  }
}

function setCachedLogoUrl(slug: string, url: string): void {
  try {
    localStorage.setItem(`${LOGO_CACHE_PREFIX}${slug}`, JSON.stringify({ url, timestamp: Date.now() }));
  } catch {
    // localStorage full or unavailable — ignore
  }
}

export function clearLogoCacheForSlugs(slugs: string[]): void {
  for (const slug of slugs) {
    if (slug) {
      try {
        localStorage.removeItem(`${LOGO_CACHE_PREFIX}${slug}`);
      } catch {
        // ignore
      }
    }
  }
}

export const fetchCustomLogo = createAsyncThunk("rest/fetchCustomLogo", async (_, { getState, dispatch }) => {
  const state = getState() as RootState;
  const contextOrg = state.context.org;
  if (!contextOrg) {
    return [];
  }

  const orgRes = await request({ url: `/org/${contextOrg}` });
  const org: Org = orgRes.data;
  const orgTags = org.tags || {};
  if (!orgTags["cpln/custom-logo"]) {
    return [];
  }

  const lightLogoSlug = orgTags["cpln/custom-logo"];
  dispatch(setLightLogoSlug(lightLogoSlug));
  const darkLogoSlug = orgTags["cpln/custom-logo-dark"] || lightLogoSlug;
  dispatch(setDarkLogoSlug(darkLogoSlug));

  const slugs = Array.from(new Set([lightLogoSlug, darkLogoSlug]));

  // 1. Check localStorage cache first
  const customLogos: CustomLogo[] = [];
  const uncachedSlugs: string[] = [];
  for (const slug of slugs) {
    const cachedUrl = getCachedLogoUrl(slug);
    if (cachedUrl) {
      customLogos.push({ slug, url: cachedUrl });
    } else {
      uncachedSlugs.push(slug);
    }
  }

  if (uncachedSlugs.length === 0) {
    return customLogos;
  }

  // 2. Try direct fetch by slug path (works for logos uploaded with slug-based filenames)
  const storage = getStorage(getFirebaseApp());
  const remainingSlugs: string[] = [];
  await Promise.all(
    uncachedSlugs.map(async (slug) => {
      try {
        const itemRef = ref(storage, `custom-logo/${slug}`);
        const url = await getDownloadURL(itemRef);
        customLogos.push({ slug, url });
        setCachedLogoUrl(slug, url);
      } catch {
        // File not found at direct path — need listAll fallback
        remainingSlugs.push(slug);
      }
    }),
  );

  if (remainingSlugs.length === 0) {
    return customLogos;
  }

  // 3. Fallback: listAll + parallel metadata scan for logos with old random filenames
  const storageRef = ref(storage, "custom-logo");
  const allLogos: ListResult = await listAll(storageRef);

  const metadataResults = await Promise.all(allLogos.items.map((item) => getMetadata(item)));

  const matchingIndices: number[] = [];
  for (let i = 0; i < allLogos.items.length; i++) {
    const slug = metadataResults[i].customMetadata?.slug;
    if (slug && remainingSlugs.includes(slug)) {
      matchingIndices.push(i);
    }
  }

  const urls = await Promise.all(matchingIndices.map((i) => getDownloadURL(allLogos.items[i])));

  for (let j = 0; j < matchingIndices.length; j++) {
    const slug = metadataResults[matchingIndices[j]].customMetadata!.slug;
    const url = urls[j];
    customLogos.push({ slug, url });
    setCachedLogoUrl(slug, url);
  }

  return customLogos;
});

export const fetchChargeableItems = createAsyncThunk("rest/fetchChargeableItems", async (_, { getState, dispatch }) => {
  console.log("@fetchChargeableItems");
  const { data } = await request({ service: "billing-ng", method: "get", url: `/chargeable_items` });
  return (data as ChargeableItem[]).filter((ci) => {
    // ignore load-balancer-byok completely
    if (ci.name === "load-balancers-byok") {
      return false;
    }
    return true;
  });
});

export const restListenerMiddleware = createListenerMiddleware();

export const restReducer = restSlice.reducer;

export const selectLightSlug = (state: RootState) => state.rest.lightSlug;
export const selectDarkSlug = (state: RootState) => state.rest.darkSlug;
export const selectCustomLogos = (state: RootState) => state.rest.customLogos;

export const selectHasCustomLogo = createSelector([selectLightSlug], (lightSlug) => {
  return !!lightSlug;
});

export const selectLightLogo = createSelector([selectCustomLogos, selectLightSlug], (customLogos, lightSlug) => {
  const customLogo = customLogos.find((c) => c.slug === lightSlug);
  if (!customLogo) {
    return { slug: "default", url: `/resources/logos/controlPlaneLogo.svg` };
  }
  return customLogo;
});

export const selectDarkLogo = createSelector([selectCustomLogos, selectDarkSlug], (customLogos, darkSlug) => {
  const customLogo = customLogos.find((c) => c.slug === darkSlug);
  if (!customLogo) {
    return { slug: "default", url: `/resources/logos/controlPlaneLogoWhite.svg` };
  }
  return customLogo;
});

export const selectLightLogoUrl = createSelector(
  [selectCustomLogos, selectLightSlug],
  (customLogos, lightSlug) => {
    if (!lightSlug) return null;
    const customLogo = customLogos.find((c) => c.slug === lightSlug);
    return customLogo?.url ?? null;
  }
);

export const selectDarkLogoUrl = createSelector(
  [selectCustomLogos, selectDarkSlug],
  (customLogos, darkSlug) => {
    if (!darkSlug) return null;
    const customLogo = customLogos.find((c) => c.slug === darkSlug);
    return customLogo?.url ?? null;
  }
);

export const selectChargeableItems = (state: RootState) => state.rest.ChargeableItems;

export const selectChargeableItemsFeatures = createSelector([selectChargeableItems], (chargeableItems) => {
  const features: string[] = [];
  for (const chargeableItem of chargeableItems) {
    if (!chargeableItem.consumptionTags) {
      continue;
    }
    if (!chargeableItem.consumptionTags.feature) {
      continue;
    }
    const feature = chargeableItem.consumptionTags.feature;
    if (features.includes(feature)) {
      continue;
    }
    features.push(feature);
  }
  return features;
});
